// src/components/kit/link-in-bio/themes.ts

import type { StylePreset, PalettePreset } from '@/types/linkInBio';

export interface StyleConfig {
  id: StylePreset;
  name: string;
  description: string;
  buttonRadius: string;
  emailRadius: string;
  headingFont: string;
  bodyFont: string;
}

export const STYLES: Record<StylePreset, StyleConfig> = {
  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Rounded corners, Inter font',
    buttonRadius: 'rounded-lg',
    emailRadius: 'rounded-lg',
    headingFont: 'font-inter',
    bodyFont: 'font-inter',
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Pill buttons, Inter font',
    buttonRadius: 'rounded-full',
    emailRadius: 'rounded-lg',
    headingFont: 'font-inter',
    bodyFont: 'font-inter',
  },
  elegant: {
    id: 'elegant',
    name: 'Elegant',
    description: 'Rounded corners, Playfair Display',
    buttonRadius: 'rounded-lg',
    emailRadius: 'rounded-lg',
    headingFont: 'font-serif-display',
    bodyFont: 'font-inter',
  },
  bold: {
    id: 'bold',
    name: 'Bold',
    description: 'Sharp corners, Space Grotesk',
    buttonRadius: 'rounded-sm',
    emailRadius: 'rounded-sm',
    headingFont: 'font-grotesk',
    bodyFont: 'font-grotesk',
  },
};

export interface PaletteConfig {
  id: PalettePreset;
  name: string;
  bg: string;
  solidBg?: string;
  gradientBg: string;
  text: string;
  subtext: string;
  accent: string;
  accentHover: string;
  accentText: string;
  accentColor: string;
  accentLight: string;
  bannerBg: string;
  bannerBorder: string;
  bannerText: string;
  cardBg: string;
  cardBorder: string;
  cardHover: string;
  cardText?: string;
  linkBg?: string;
  inputBg: string;
  inputBorder: string;
  inputFocus: string;
  placeholder: string;
  avatarBorder: string;
  footerBorder: string;
  footerText: string;
  reportText: string;
  reportHover: string;
  socialColor: string;
  isLight?: boolean;
  isGlass?: boolean;
  accentBorder?: string;
}

export const PALETTES: Record<PalettePreset, PaletteConfig> = {
  'dark-gold': {
    id: 'dark-gold',
    name: 'Dark Gold',
    bg: 'bg-[rgb(27,27,27)]',
    gradientBg: 'background: linear-gradient(160deg, #1a1a1a 0%, #2d2006 50%, #1a1a1a 100%)',
    text: 'text-white',
    subtext: 'text-gray-400',
    accent: 'bg-yellow-500',
    accentHover: 'hover:bg-yellow-400',
    accentText: 'text-black',
    accentColor: 'text-yellow-500',
    accentLight: 'text-yellow-400',
    bannerBg: 'bg-yellow-500/10',
    bannerBorder: 'border-yellow-500/30',
    bannerText: 'text-yellow-400',
    cardBg: 'bg-[rgb(64,64,64)]',
    cardBorder: 'border-[rgb(38,38,38)]',
    cardHover: 'hover:bg-[rgb(115,115,115)]',
    inputBg: 'bg-[rgb(115,115,115)]',
    inputBorder: 'border-[rgb(82,82,82)]',
    inputFocus: 'focus:border-yellow-500',
    placeholder: 'placeholder-gray-400',
    avatarBorder: 'border-yellow-500/30',
    footerBorder: 'border-[rgb(38,38,38)]',
    footerText: 'text-gray-500',
    reportText: 'text-gray-600',
    reportHover: 'hover:text-gray-400',
    socialColor: 'text-yellow-400',
  },
  'dark-blue': {
    id: 'dark-blue',
    name: 'Dark Blue',
    bg: 'bg-slate-900',
    gradientBg: 'background: linear-gradient(160deg, #0f172a 0%, #0c1e3d 50%, #143266 100%)',
    text: 'text-white',
    subtext: 'text-slate-400',
    accent: 'bg-blue-500',
    accentHover: 'hover:bg-blue-400',
    accentText: 'text-white',
    accentColor: 'text-blue-500',
    accentLight: 'text-blue-400',
    bannerBg: 'bg-blue-500/10',
    bannerBorder: 'border-blue-500/30',
    bannerText: 'text-blue-400',
    cardBg: 'bg-[rgb(64,64,64)]',
    cardBorder: 'border-[rgb(38,38,38)]',
    cardHover: 'hover:bg-[rgb(115,115,115)]',
    inputBg: 'bg-[rgb(115,115,115)]',
    inputBorder: 'border-[rgb(82,82,82)]',
    inputFocus: 'focus:border-blue-500',
    placeholder: 'placeholder-slate-400',
    avatarBorder: 'border-blue-500/30',
    footerBorder: 'border-slate-800',
    footerText: 'text-slate-500',
    reportText: 'text-slate-600',
    reportHover: 'hover:text-slate-400',
    socialColor: 'text-blue-400',
  },
  'dark-red': {
    id: 'dark-red',
    name: 'Dark Red',
    bg: 'bg-zinc-900',
    gradientBg: 'background: linear-gradient(160deg, #18181b 0%, #2d0a0a 50%, #4d1111 100%)',
    text: 'text-white',
    subtext: 'text-zinc-400',
    accent: 'bg-red-600',
    accentHover: 'hover:bg-red-500',
    accentText: 'text-white',
    accentColor: 'text-red-500',
    accentLight: 'text-red-400',
    bannerBg: 'bg-red-600/10',
    bannerBorder: 'border-red-600/30',
    bannerText: 'text-red-400',
    cardBg: 'bg-zinc-800',
    cardBorder: 'border-zinc-700',
    cardHover: 'hover:bg-zinc-700',
    inputBg: 'bg-zinc-700',
    inputBorder: 'border-zinc-600',
    inputFocus: 'focus:border-red-500',
    placeholder: 'placeholder-zinc-400',
    avatarBorder: 'border-red-600/30',
    footerBorder: 'border-zinc-800',
    footerText: 'text-zinc-500',
    reportText: 'text-zinc-600',
    reportHover: 'hover:text-zinc-400',
    socialColor: 'text-red-400',
  },
  'dark-emerald': {
    id: 'dark-emerald',
    name: 'Dark Emerald',
    bg: 'bg-gray-900',
    gradientBg: 'background: linear-gradient(160deg, #111827 0%, #052e16 50%, #1c2740 100%)',
    text: 'text-white',
    subtext: 'text-gray-400',
    accent: 'bg-emerald-500',
    accentHover: 'hover:bg-emerald-400',
    accentText: 'text-black',
    accentColor: 'text-emerald-500',
    accentLight: 'text-emerald-400',
    bannerBg: 'bg-emerald-500/10',
    bannerBorder: 'border-emerald-500/30',
    bannerText: 'text-emerald-400',
    cardBg: 'bg-gray-800',
    cardBorder: 'border-gray-700',
    cardHover: 'hover:bg-gray-700',
    inputBg: 'bg-gray-700',
    inputBorder: 'border-gray-600',
    inputFocus: 'focus:border-emerald-500',
    placeholder: 'placeholder-gray-400',
    avatarBorder: 'border-emerald-500/30',
    footerBorder: 'border-gray-800',
    footerText: 'text-gray-500',
    reportText: 'text-gray-600',
    reportHover: 'hover:text-gray-400',
    socialColor: 'text-emerald-400',
  },
  'light-minimal': {
    id: 'light-minimal',
    name: 'Light Minimal',
    bg: 'bg-white',
    gradientBg: 'background: linear-gradient(160deg, #ffffff 0%, #f3f4f6 50%, #cacbcc 100%)',
    text: 'text-gray-900',
    subtext: 'text-gray-500',
    accent: 'bg-gray-900',
    accentHover: 'hover:bg-gray-700',
    accentText: 'text-white',
    accentColor: 'text-gray-900',
    accentLight: 'text-gray-900',
    bannerBg: 'bg-gray-50',
    bannerBorder: 'border-gray-200',
    bannerText: 'text-gray-700',
    cardBg: 'bg-gray-50',
    cardBorder: 'border-gray-400',
    cardHover: 'hover:bg-gray-50',
    cardText: 'text-gray-900',
    linkBg: 'bg-white',
    inputBg: 'bg-white',
    inputBorder: 'border-gray-400',
    inputFocus: 'focus:border-gray-900',
    placeholder: 'placeholder-gray-400',
    avatarBorder: 'border-gray-200',
    footerBorder: 'border-gray-100',
    footerText: 'text-gray-400',
    reportText: 'text-gray-400',
    reportHover: 'hover:text-gray-600',
    isLight: true,
    socialColor: 'text-gray-900',
  },
  'light-warm': {
    id: 'light-warm',
    name: 'Light Warm',
    bg: 'bg-amber-50',
    gradientBg: 'background: linear-gradient(160deg, #fffbeb 0%, #fff2bf 50%, #ffe680 100%)',
    text: 'text-amber-900',
    subtext: 'text-amber-700/70',
    accent: 'bg-amber-700',
    accentHover: 'hover:bg-amber-600',
    accentText: 'text-white',
    accentColor: 'text-amber-800',
    accentLight: 'text-amber-800',
    bannerBg: 'bg-amber-100',
    bannerBorder: 'border-amber-200',
    bannerText: 'text-amber-800',
    cardBg: 'bg-white',
    cardBorder: 'border-amber-200',
    cardHover: 'hover:bg-amber-50',
    cardText: 'text-amber-900',
    linkBg: 'bg-white',
    inputBg: 'bg-amber-50',
    inputBorder: 'border-amber-200',
    inputFocus: 'focus:border-amber-700',
    placeholder: 'placeholder-amber-400',
    avatarBorder: 'border-amber-300',
    footerBorder: 'border-amber-200',
    footerText: 'text-amber-600',
    reportText: 'text-amber-500',
    reportHover: 'hover:text-amber-700',
    isLight: true,
    socialColor: 'text-amber-900',
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset',
    bg: 'bg-purple-900',
    solidBg: 'background: #581c87',
    gradientBg: 'background: linear-gradient(to bottom, #581c87, #9d174d, #c2410c)',
    text: 'text-white',
    subtext: 'text-white/70',
    accent: 'bg-white/20',
    accentHover: 'hover:bg-white/30',
    accentText: 'text-white',
    accentColor: 'text-white/80',
    accentLight: 'text-white/80',
    bannerBg: 'bg-white/10',
    bannerBorder: 'border-white/20',
    bannerText: 'text-white',
    cardBg: 'bg-white/10',
    cardBorder: 'border-white/20',
    cardHover: 'hover:bg-white/20',
    inputBg: 'bg-white/10',
    inputBorder: 'border-white/20',
    inputFocus: 'focus:border-white/50',
    placeholder: 'placeholder-white/50',
    avatarBorder: 'border-white/30',
    footerBorder: 'border-white/10',
    footerText: 'text-white/50',
    reportText: 'text-white/40',
    reportHover: 'hover:text-white/70',
    isGlass: true,
    accentBorder: 'border border-white/40',
    socialColor: 'text-white',
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    bg: 'bg-cyan-950',
    solidBg: 'background: #083344',
    gradientBg: 'background: linear-gradient(160deg, #083344 0%, #0f4c5c 40%, #0e7490 100%)',
    text: 'text-white',
    subtext: 'text-cyan-300/70',
    accent: 'bg-teal-400',
    accentHover: 'hover:bg-teal-300',
    accentText: 'text-gray-900',
    accentColor: 'text-teal-400',
    accentLight: 'text-teal-300',
    bannerBg: 'bg-teal-400/10',
    bannerBorder: 'border-teal-400/30',
    bannerText: 'text-teal-300',
    cardBg: 'bg-cyan-900/50',
    cardBorder: 'border-cyan-700/50',
    cardHover: 'hover:bg-cyan-800/50',
    inputBg: 'bg-cyan-900/50',
    inputBorder: 'border-cyan-700/50',
    inputFocus: 'focus:border-teal-400',
    placeholder: 'placeholder-cyan-400/50',
    avatarBorder: 'border-teal-400/30',
    footerBorder: 'border-cyan-800/50',
    footerText: 'text-cyan-400/50',
    reportText: 'text-cyan-500/40',
    reportHover: 'hover:text-cyan-300/70',
    socialColor: 'text-teal-300',
  },
};
